import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, RefreshCw, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminUtm — attribution dashboard for the tier-up email "Share on X"
 * campaigns and any other UTM-tagged inbound traffic.
 *
 * Backend: GET /api/admin/analytics/utm-summary?days=N
 */
export default function AdminUtm() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/utm-summary", { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load UTM summary");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [days]);

  return (
    <div className="space-y-5" data-testid="admin-utm">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-secondary" strokeWidth={1.75} />
            <span className="label-mystic text-secondary">Attribution</span>
          </div>
          <h2 className="font-serif text-3xl mt-1">UTM Campaign Hits</h2>
          <p className="text-sm text-foreground/65 mt-1 max-w-xl">
            Anonymous click tracking for tier-up "Share on X" emails and any
            other UTM-tagged inbound links. Signups attributed via `ref`
            referral codes appear in the second table.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-sm border border-secondary/30 overflow-hidden text-[11px]">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 uppercase tracking-[0.18em] ${
                  days === d
                    ? "bg-secondary text-secondary-foreground"
                    : "text-foreground/65 hover:bg-secondary/[0.08]"
                }`}
                data-testid={`admin-utm-window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-secondary/30 text-xs uppercase tracking-[0.18em] text-foreground/75 hover:bg-secondary/[0.08] disabled:opacity-50"
            data-testid="admin-utm-refresh"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Refresh
          </button>
        </div>
      </div>

      {!data && loading && <div className="text-xs text-foreground/55">Loading…</div>}

      {data && (
        <>
          <div className="parchment p-4 flex items-center gap-3" data-testid="admin-utm-total">
            <Activity size={14} className="text-secondary" />
            <span className="label-mystic text-secondary text-[10px]">Window {data.windowDays}d</span>
            <span className="text-foreground/40">·</span>
            <span className="font-serif text-2xl text-secondary">{data.totalHits.toLocaleString()}</span>
            <span className="text-xs text-foreground/55">total UTM-tagged hits</span>
          </div>

          <div className="parchment p-4" data-testid="admin-utm-by-campaign">
            <div className="label-mystic text-secondary/80 text-[10px] mb-3">By Campaign</div>
            {(!data.byCampaign || data.byCampaign.length === 0) && (
              <div className="text-xs text-foreground/55">No campaign-tagged hits in this window yet.</div>
            )}
            {(data.byCampaign || []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-foreground/55 text-[10px] uppercase tracking-[0.18em] border-b border-secondary/15">
                      <th className="text-left py-2">Campaign</th>
                      <th className="text-left py-2">Source</th>
                      <th className="text-left py-2">Medium</th>
                      <th className="text-right py-2">Clicks</th>
                      <th className="text-right py-2">Unique Visitors</th>
                      <th className="text-right py-2">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byCampaign.map((c, i) => (
                      <tr key={i} className="border-b border-secondary/8 last:border-0" data-testid={`admin-utm-campaign-row-${i}`}>
                        <td className="py-2 text-foreground">{c.campaign || <span className="text-foreground/40">—</span>}</td>
                        <td className="py-2 text-foreground/75">{c.source || "—"}</td>
                        <td className="py-2 text-foreground/75">{c.medium || "—"}</td>
                        <td className="py-2 text-right text-secondary font-mono">{c.clicks.toLocaleString()}</td>
                        <td className="py-2 text-right text-foreground/65 font-mono">{c.uniqueVisitors.toLocaleString()}</td>
                        <td className="py-2 text-right text-foreground/45 text-[11px]">{formatAgo(c.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="parchment p-4" data-testid="admin-utm-top-referrers">
            <div className="label-mystic text-secondary/80 text-[10px] mb-3">Top Referrers (signups attributed)</div>
            {(!data.topReferrers || data.topReferrers.length === 0) && (
              <div className="text-xs text-foreground/55">No referred signups in this window yet.</div>
            )}
            {(data.topReferrers || []).length > 0 && (
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-foreground/55 text-[10px] uppercase tracking-[0.18em] border-b border-secondary/15">
                    <th className="text-left py-2">Referrer</th>
                    <th className="text-left py-2">Code</th>
                    <th className="text-right py-2">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topReferrers.map((r, i) => (
                    <tr key={i} className="border-b border-secondary/8 last:border-0" data-testid={`admin-utm-referrer-row-${i}`}>
                      <td className="py-2 text-foreground">{r.referrerName}</td>
                      <td className="py-2 text-foreground/65 font-mono">{r.referralCode || "—"}</td>
                      <td className="py-2 text-right text-secondary font-mono">{r.signups}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatAgo(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch { return iso; }
}
